import { ReactElement, useMemo, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import type { TokenSpendOut } from '@api/fastAPI.schemas'
import { useGetTokenSpend } from '@api/user-management/user-management'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import StatTile from './components/StatTile'
import UserTokenSpendRow from './components/UserTokenSpendRow'
import {
  formatCompactTokens,
  formatCost,
  formatTokens,
  Period,
  periodConfig,
  periodStart,
  sumTokenSpend,
} from './utils'

const TokenSpendDashboard = (): ReactElement => {
  const [period, setPeriod] = useState<Period>('month')
  const { enqueueSnackbar } = useSnackbar()

  // Computed once per period switch so the query key stays stable.
  const since = useMemo(() => periodStart(period), [period])

  const { data, isLoading } = useGetTokenSpend<TokenSpendOut, AxiosError>(
    since ? { since } : undefined,
    {
      query: {
        keepPreviousData: true,
        onError: error => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der KI-Nutzung'), {
            variant: 'error',
          })
        },
      },
    },
  )

  const users = useMemo(() => data?.users ?? [], [data])
  const totals = useMemo(() => sumTokenSpend(users), [users])
  const maxCostUsd = users[0]?.cost_usd ?? 0

  return (
    <Box display="flex" flexDirection="column" gap={2} data-cy="token-spend-dashboard">
      <Box display="flex" justifyContent="flex-end">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_, value: Period | null) => value && setPeriod(value)}
        >
          {Object.entries(periodConfig).map(([value, { label }]) => (
            <ToggleButton key={value} value={value} data-cy={`token-spend-period-${value}`}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <Box display="flex" gap={2} flexWrap="wrap">
            <StatTile
              label="Kosten"
              value={(totals.runsWithoutCost > 0 ? '≥ ' : '') + formatCost(totals.costUsd)}
              caption={
                totals.runsWithoutCost > 0
                  ? `${totals.runsWithoutCost} Turn(s) ohne bekannte Kosten`
                  : undefined
              }
              data-cy="token-spend-total-cost"
            />
            <StatTile
              label="Tokens"
              value={formatCompactTokens(totals.inputTokens + totals.outputTokens)}
              caption={`${formatTokens(totals.inputTokens)} Input / ${formatTokens(
                totals.outputTokens,
              )} Output`}
            />
            <StatTile label="Aktive Nutzer" value={formatTokens(users.length)} />
            <StatTile label="Chat-Turns" value={formatTokens(totals.runs)} />
          </Box>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="token spend table" data-cy="token-spend-list">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Nutzer</TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell align="right">Turns</TableCell>
                  <TableCell align="right">Input / Output Tokens</TableCell>
                  <TableCell align="right">Kosten</TableCell>
                  <TableCell>Zuletzt aktiv</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                      Keine KI-Nutzung im gewählten Zeitraum
                    </TableCell>
                  </TableRow>
                )}
                {users.map(user => (
                  <UserTokenSpendRow key={user.user_id} user={user} maxCostUsd={maxCostUsd} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  )
}

export default TokenSpendDashboard
